import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileQuestion, ChevronLeft, ChevronRight, Download } from 'lucide-react';

function formatBytes(n) {
  if (!n && n !== 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
const AUDIO_EXT = ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a'];
const IMAGE_KINDS = ['image_post', 'thumbnail', 'carousel', 'story', 'promo_post'];

/**
 * Asset preview (section 6) — only ever shows a file this browser has a
 * real reference for. Video/live/replay assets resolve the same presigned
 * `stream_url` the Editor already uses; local design assets (no backend
 * record) render straight from their own saved pages. No metadata is
 * fabricated — fields with nothing available are simply omitted.
 */
export function AssetPreviewDialog({ asset, open, onOpenChange, apiUrl }) {
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);

  useEffect(() => {
    setStreamUrl(null); setStreamError(null); setPageIdx(0);
    if (!open || !asset || asset.localOnly) return;
    setLoading(true);
    fetch(`${apiUrl}/api/editor/projects/source/${asset.id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setStreamUrl(d.stream_url || null); setStreamError(d.stream_error || (!d.stream_url ? 'No playable file found for this asset yet.' : null)); })
      .catch(() => setStreamError('Could not reach the server to load this preview.'))
      .finally(() => setLoading(false));
  }, [open, asset, apiUrl]);

  if (!asset) return null;
  const isAudio = AUDIO_EXT.includes((asset.name || '').split('.').pop()?.toLowerCase());
  const isServerVideo = !asset.localOnly;
  const isDesign = asset.localOnly;
  const pages = asset.pages || [];
  const hasMultiplePages = pages.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl" data-testid="asset-preview-dialog">
        <DialogHeader><DialogTitle className="text-white truncate">{asset.name}</DialogTitle></DialogHeader>

        <div className="aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center relative" data-testid="asset-preview-media">
          {isServerVideo && loading && <Loader2 className="h-6 w-6 text-[#8A93A6] animate-spin" />}
          {isServerVideo && !loading && isAudio && streamUrl && <audio src={streamUrl} controls className="w-full px-6" />}
          {isServerVideo && !loading && !isAudio && streamUrl && <video src={streamUrl} controls className="w-full h-full" data-testid="asset-preview-video-player" />}
          {isServerVideo && !loading && !streamUrl && (
            <div className="text-center p-6"><FileQuestion className="h-8 w-8 text-[#555E6E] mx-auto mb-2" /><p className="text-xs text-[#8A93A6]">{streamError}</p></div>
          )}
          {isDesign && pages.length > 0 && (
            <>
              <div className="w-full h-full relative" style={{ background: pages[pageIdx]?.background || '#000' }}>
                {pages[pageIdx]?.elements?.filter((e) => e.type === 'image' && e.src).map((e) => (
                  <img key={e.id} src={e.src} alt="" className="absolute object-cover" style={{ left: `${(e.x || 0) * 100}%`, top: `${(e.y || 0) * 100}%`, width: `${(e.w || 1) * 100}%`, height: `${(e.h || 1) * 100}%` }} />
                ))}
              </div>
              {hasMultiplePages && (
                <>
                  <button onClick={() => setPageIdx((i) => Math.max(0, i - 1))} disabled={pageIdx === 0} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1.5 text-white disabled:opacity-30" data-testid="asset-preview-page-prev"><ChevronLeft className="h-4 w-4" /></button>
                  <button onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))} disabled={pageIdx === pages.length - 1} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 rounded-full p-1.5 text-white disabled:opacity-30" data-testid="asset-preview-page-next"><ChevronRight className="h-4 w-4" /></button>
                  <Badge className="absolute bottom-2 right-2 bg-black/60 border-none text-white text-[10px]">Page {pageIdx + 1} of {pages.length}</Badge>
                </>
              )}
            </>
          )}
          {isDesign && pages.length === 0 && (
            <div className="text-center p-6"><FileQuestion className="h-8 w-8 text-[#555E6E] mx-auto mb-2" /><p className="text-xs text-[#8A93A6]">No preview available for this design.</p></div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-testid="asset-preview-metadata">
          <Row label="Type" value={asset.localOnly ? `${asset.assetKind} (saved in this browser)` : asset.type} />
          <Row label="Project" value={asset.projectName || '—'} />
          {asset.fileSize != null && <Row label="File size" value={formatBytes(asset.fileSize)} />}
          {asset.createdAt && <Row label="Created" value={new Date(asset.createdAt).toLocaleString()} />}
          {asset.aspectRatio && <Row label="Aspect ratio" value={asset.aspectRatio} />}
          <Row label="Storage status" value={asset.localOnly ? 'Browser-local only — not yet saved to a server' : 'Backblaze B2'} />
          {asset.variantCount > 1 && <Row label="Platform variants" value={asset.variantCount} />}
        </div>

        {isServerVideo && streamUrl && (
          <a href={streamUrl} download className="inline-block"><Button size="sm" variant="outline" className="border-white/10" data-testid="asset-preview-download-btn"><Download className="h-3.5 w-3.5 mr-1.5" />Download</Button></a>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return <div><span className="text-[#555E6E]">{label}: </span><span className="text-[#C0C7D4]">{value}</span></div>;
}
